'use client';

/**
 * Renders a lucide icon by its kebab-case DB name.
 *
 * `commerce_categories.icon_name` stores values like `footprints`, `shirt`,
 * `graduation-cap`. lucide-react exports those as PascalCase components
 * (`Footprints`, `Shirt`, `GraduationCap`). This helper bridges the gap
 * with a defensive lookup: unknown names return `null` instead of crashing.
 */
import type { ComponentType } from 'react';
import * as LucideIcons from 'lucide-react';
import type { LucideProps } from 'lucide-react';

function toPascal(kebab: string): string {
  return kebab
    .split('-')
    .filter(Boolean)
    .map((segment) => segment[0]!.toUpperCase() + segment.slice(1))
    .join('');
}

interface CategoryIconProps {
  /** Kebab-case lucide name from commerce_categories.icon_name. */
  name: string | null;
  /** Tailwind classes (size, color, etc.). */
  className?: string;
}

export function CategoryIcon({ name, className }: CategoryIconProps) {
  if (!name) return null;
  const key = toPascal(name);
  const Icon = (LucideIcons as Record<string, unknown>)[key] as
    | ComponentType<LucideProps>
    | undefined;
  if (!Icon) return null;
  return <Icon className={className} aria-hidden="true" />;
}
